import { flattenStructureLeaves } from "./export-structure.js";
/** Every fillable slot (field leaf) of a format structure, in document order. */
export function structureSlots(nodes) {
    return flattenStructureLeaves(nodes).map(({ node, path }) => {
        const slot = {
            path,
            name: node.name,
            required: node.required ?? false,
            allowEmpty: node.allowEmpty ?? true,
        };
        if (node.dataType !== undefined)
            slot.dataType = node.dataType;
        if (node.format !== undefined)
            slot.format = node.format;
        if (node.length !== undefined)
            slot.length = node.length;
        return slot;
    });
}
/** A destination mapping (legacy or Slice-4 shape) read as a normalized
 *  fulfillment. Legacy back-compat: slotPath ??= targetColumn, kind ??= "field"
 *  (a blank legacy sourceField stays "field" → resolves to unfulfilled, NOT an
 *  explicit empty — explicit "empty" only ever comes from the new 4c UI). */
export function toSlotFulfillment(mapping) {
    const out = {
        slotPath: mapping.slotPath ?? mapping.targetColumn,
        kind: mapping.kind ?? "field",
    };
    if (mapping.sourceField !== undefined)
        out.sourceField = mapping.sourceField;
    if (mapping.constantValue !== undefined)
        out.constantValue = mapping.constantValue;
    return out;
}
/** Declarative/static fulfillment check — "does a binding exist for this slot?"
 *  (value-level conformance stays with Tier-1 rules; this is the publish-gate /
 *  coverage notion). An explicit `empty` only fulfils a slot that allows empty. */
export function isSlotFulfilled(slot, f) {
    if (!f)
        return false;
    switch (f.kind) {
        case "field":
        case "calculated":
            return !!f.sourceField?.trim();
        case "constant":
            return f.constantValue != null;
        case "empty":
            return slot.allowEmpty === true;
        default:
            return false;
    }
}
/** Coverage of a format's slots by a destination's mappings, keyed by slot path. */
export function computeSlotCoverage(nodes, mappings) {
    const byPath = new Map();
    for (const m of mappings) {
        const f = toSlotFulfillment(m);
        byPath.set(f.slotPath, f);
    }
    const slots = structureSlots(nodes);
    const perSlot = slots.map((slot) => {
        const fulfillment = byPath.get(slot.path);
        return { slot, fulfillment, fulfilled: isSlotFulfilled(slot, fulfillment) };
    });
    const required = perSlot.filter((p) => p.slot.required);
    const unfulfilledRequired = required.filter((p) => !p.fulfilled).map((p) => p.slot);
    return {
        total: slots.length,
        requiredTotal: required.length,
        requiredFulfilled: required.length - unfulfilledRequired.length,
        unfulfilledRequired,
        perSlot,
    };
}
//# sourceMappingURL=slot-fulfillment.js.map